<template>
  <div class="base_data_table">
    <VuetifyDataTable
      v-bind="tableAttrs"
      v-on="tableListeners"
      :items="items"
      :server-items-length="resolvedServerItemsLength"
      :page="localPage"
      :items-per-page="localItemsPerPage"
      :options="localOptions"
      :hide-default-footer="resolvedHideDefaultFooter"
      @update:page="onPageUpdated"
      @update:items-per-page="onItemsPerPageUpdated"
      @update:options="onOptionsUpdated"
    >
      <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
        <slot
          v-if="slot !== 'footer' && slot !== 'foot'"
          :name="slot"
          v-bind="scope"
        />
      </template>

      <template v-slot:foot>
        <tr>
          <td colspan="100%" class="base_data_table_footer_cell">
            <slot
              name="footer"
              :page="localPage"
              :items-per-page="localItemsPerPage"
              :total-items="totalItems"
              :items-per-page-options="resolvedItemsPerPageOptions"
              :total-pages="totalPages"
              :set-page="onPageUpdated"
              :set-items-per-page="onItemsPerPageUpdated"
            >
              <PaginationFooter
                :page="localPage"
                :items-per-page="localItemsPerPage"
                :total-items="totalItems"
                :items-per-page-options="resolvedItemsPerPageOptions"
                :disabled="paginationFooterDisabled"
                @update:page="onPageUpdated"
                @update:items-per-page="onItemsPerPageUpdated"
              />
            </slot>
          </td>
        </tr>
      </template>
    </VuetifyDataTable>
  </div>
</template>

<script>
import { VDataTable } from "vuetify/lib/components/VDataTable";
import PaginationFooter from "./PaginationFooter.vue";

export default {
  name: "BaseDataTable",
  components: {
    VuetifyDataTable: VDataTable,
    PaginationFooter
  },
  inheritAttrs: false,
  props: {
    page: {
      type: Number,
      default: 1
    },
    itemsPerPage: {
      type: Number,
      default: 25
    },
    options: {
      type: Object,
      default: () => ({})
    },
    items: {
      type: Array,
      default: () => []
    },
    serverItemsLength: {
      type: Number,
      default: -1
    },
    footerProps: {
      type: Object,
      default: () => ({})
    },
    paginationFooterDisabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const normalizedOptions = this.normalizeOptions(this.options);
    const localItemsPerPage = this.normalizeItemsPerPage(
      this.itemsPerPage,
      normalizedOptions.itemsPerPage
    );
    const localPage = this.normalizePage(this.page, normalizedOptions.page);

    return {
      localPage,
      localItemsPerPage,
      localOptions: {
        ...normalizedOptions,
        page: localPage,
        itemsPerPage: localItemsPerPage
      }
    };
  },
  mounted() {
    this.emitCurrentState();
  },
  computed: {
    tableAttrs() {
      const attrs = { ...this.$attrs };
      delete attrs["hide-default-footer"];
      return attrs;
    },
    incomingHideDefaultFooter() {
      const hideDefaultFooterValue = this.$attrs["hide-default-footer"];
      if (
        hideDefaultFooterValue === "" ||
        hideDefaultFooterValue === true ||
        hideDefaultFooterValue === "true"
      ) {
        return true;
      }
      return false;
    },
    resolvedHideDefaultFooter() {
      return this.incomingHideDefaultFooter;
    },
    tableListeners() {
      const listeners = { ...this.$listeners };
      delete listeners["update:page"];
      delete listeners["update:items-per-page"];
      delete listeners["update:options"];
      return listeners;
    },
    resolvedServerItemsLength() {
      const value = Number(this.serverItemsLength);
      if (Number.isFinite(value) && value >= 0) {
        return value;
      }
      return -1;
    },
    totalItems() {
      if (this.resolvedServerItemsLength >= 0) {
        return this.resolvedServerItemsLength;
      }
      return Array.isArray(this.items) ? this.items.length : 0;
    },
    totalPages() {
      if (this.localItemsPerPage === -1) return 1;
      const count = Math.ceil(this.totalItems / this.localItemsPerPage);
      return Math.max(count, 1);
    },
    resolvedItemsPerPageOptions() {
      const fromFooterProps =
        this.footerProps["items-per-page-options"] ||
        this.footerProps.itemsPerPageOptions;

      if (Array.isArray(fromFooterProps) && fromFooterProps.length) {
        return fromFooterProps;
      }
      if (
        Array.isArray(this.$pageLimitOptions) &&
        this.$pageLimitOptions.length
      ) {
        return this.$pageLimitOptions;
      }
      return [10, 25, 50, 100];
    }
  },
  watch: {
    page(nextPage) {
      const normalizedPage = this.normalizePage(nextPage, this.localPage);
      if (normalizedPage !== this.localPage) {
        this.localPage = normalizedPage;
        this.localOptions = {
          ...this.localOptions,
          page: normalizedPage
        };
      }
    },
    itemsPerPage(nextItemsPerPage) {
      const normalizedItemsPerPage = this.normalizeItemsPerPage(
        nextItemsPerPage,
        this.localItemsPerPage
      );
      if (normalizedItemsPerPage !== this.localItemsPerPage) {
        this.localItemsPerPage = normalizedItemsPerPage;
        this.localOptions = {
          ...this.localOptions,
          itemsPerPage: normalizedItemsPerPage
        };
      }
    },
    options: {
      deep: true,
      handler(nextOptions) {
        const normalizedOptions = this.normalizeOptions(nextOptions);
        this.localPage = this.normalizePage(
          normalizedOptions.page,
          this.localPage
        );
        this.localItemsPerPage = this.normalizeItemsPerPage(
          normalizedOptions.itemsPerPage,
          this.localItemsPerPage
        );
        this.localOptions = {
          ...normalizedOptions,
          page: this.localPage,
          itemsPerPage: this.localItemsPerPage
        };
      }
    }
  },
  methods: {
    emitCurrentState() {
      this.$emit("update:page", this.localPage);
      this.$emit("update:items-per-page", this.localItemsPerPage);
      this.$emit("update:options", { ...this.localOptions });
    },
    normalizePage(value, fallback = 1) {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue) && numericValue > 0) {
        return numericValue;
      }
      return fallback;
    },
    normalizeItemsPerPage(value, fallback = 25) {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue) && numericValue !== 0) {
        return numericValue;
      }
      return fallback;
    },
    normalizeOptions(options = {}) {
      const normalizedOptions =
        options && typeof options === "object" ? { ...options } : {};
      normalizedOptions.page = this.normalizePage(normalizedOptions.page, 1);
      normalizedOptions.itemsPerPage = this.normalizeItemsPerPage(
        normalizedOptions.itemsPerPage,
        25
      );
      return normalizedOptions;
    },
    onPageUpdated(page) {
      const targetPage = this.normalizePage(page, this.localPage);
      if (targetPage === this.localPage) return;

      this.localPage = targetPage;
      this.localOptions = {
        ...this.localOptions,
        page: targetPage,
        itemsPerPage: this.localItemsPerPage
      };

      this.$emit("update:page", targetPage);
      this.$emit("update:options", { ...this.localOptions });
    },
    onItemsPerPageUpdated(itemsPerPage) {
      const targetItemsPerPage = this.normalizeItemsPerPage(
        itemsPerPage,
        this.localItemsPerPage
      );
      if (targetItemsPerPage === this.localItemsPerPage) return;

      this.localItemsPerPage = targetItemsPerPage;
      this.localPage = this.normalizePage(1, this.localPage);
      this.localOptions = {
        ...this.localOptions,
        page: this.localPage,
        itemsPerPage: targetItemsPerPage
      };

      this.$emit("update:items-per-page", targetItemsPerPage);
      this.$emit("update:page", this.localPage);
      this.$emit("update:options", { ...this.localOptions });
    },
    onOptionsUpdated(options) {
      const normalizedOptions = this.normalizeOptions(options);
      const nextPage = this.normalizePage(
        normalizedOptions.page,
        this.localPage
      );
      const nextItemsPerPage = this.normalizeItemsPerPage(
        normalizedOptions.itemsPerPage,
        this.localItemsPerPage
      );
      const pageChanged = nextPage !== this.localPage;
      const itemsPerPageChanged = nextItemsPerPage !== this.localItemsPerPage;

      this.localPage = nextPage;
      this.localItemsPerPage = nextItemsPerPage;
      this.localOptions = {
        ...normalizedOptions,
        page: nextPage,
        itemsPerPage: nextItemsPerPage
      };

      this.$emit("update:options", { ...this.localOptions });
      if (pageChanged) this.$emit("update:page", this.localPage);
      if (itemsPerPageChanged) {
        this.$emit("update:items-per-page", this.localItemsPerPage);
      }
    }
  }
};
</script>

<style scoped>
.base_data_table_footer_cell {
  padding: 0 !important;
}
</style>
